<template>
  <div
    id="app"
    :class="`flex flex-col font-main relative ${bgClass}`"
    :style="styleVars"
  >
    <nav-bar />
    <div class="bg-main flex-1 overflow-y-auto">
      <router-view v-slot="{ Component, route }">
        <transition
          mode="out-in"
          :enter-active-class="route.meta.enterClass"
          :leave-active-class="route.meta.leaveClass"
        >
          <component :is="Component" />
        </transition>
      </router-view>
    </div>
    <router-view
      v-if="layoutConfig?.footer?.enabled"
      name="footer"
    ></router-view>
    <div v-html="customCSS"></div>
    <account-modal />
    <claim-reward-modal />
    <idle-modal />
    <landing-restriction-modal v-if="PLATFORM === 'embed'" />
  </div>
</template>

<script>
import NavBar from "@/components/NavBar";
import { mapActions, mapState, mapWritableState } from "pinia";
import { useAppStore } from "@/app-store/app";
import AccountModal from "@/components/Account/AccountModal";
import ClaimRewardModal from "@/components/ClaimRewardModal";
import { getLayoutStyling } from "@/utils/Styling";
import { useSessionStore } from "@/app-store/session";
import IdleModal from "@/components/IdleModal";
import { useOrderStore } from "@/app-store/order";
import { PLATFORM } from "@/utils/constants";
import { H } from "highlight.run";
import LandingRestrictionModal from "@/components/LandingRestrictionModal.vue";

export default {
  components: {
    IdleModal,
    ClaimRewardModal,
    AccountModal,
    NavBar,
    LandingRestrictionModal,
  },
  computed: {
    ...mapWritableState(useAppStore, [
      "layoutConfig",
      "initialLoad",
      "shop",
      "landingRestriction",
      "isLandingRestrictionOpen",
    ]),
    ...mapState(useOrderStore, ["loadingTransaction"]),
    ...mapWritableState(useSessionStore, [
      "isFirstInteractionPerformed",
      "isActivityPromptOpen",
      "userInfo",
    ]),
    ...mapWritableState(useOrderStore, ["orderType"]),
    PLATFORM() {
      return PLATFORM;
    },
    bgClass() {
      const { layoutConfig } = this;
      if (!layoutConfig || !layoutConfig.theme) return "bg-color-second";
      return `bg-color-${layoutConfig.theme.pageBackgroundColor}`;
    },
    styleVars() {
      const { layoutConfig, screenYOverflow } = this;
      return {
        ...getLayoutStyling(layoutConfig, screenYOverflow),
        height: this.getSafeViewport().height,
      };
    },
    customCSS() {
      return `<style>${
        this.layoutConfig &&
        this.layoutConfig.theme &&
        this.layoutConfig.theme.importFonts
          ? this.layoutConfig.theme.importFonts
          : ""
      }</style>`;
    },
  },
  data() {
    return {
      timeout: null,
      baseHeight: "100%",
    };
  },
  methods: {
    ...mapActions(useAppStore, ["getAppInfo", "shop"]),
    active() {
      if (this.isFirstInteractionPerformed) this.setIdleCheck();
    },
    setIdleCheck() {
      if (this.timeout) clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        if (!this.loadingTransaction) {
          this.isActivityPromptOpen = true;
        } else {
          this.setIdleCheck();
        }
      }, 40000);
    },
    getSafeViewport() {
      if (window.visualViewport) {
        return {
          width: window.visualViewport.width,
          height: window.visualViewport.height,
          offsetTop: window.visualViewport.offsetTop,
          offsetLeft: window.visualViewport.offsetLeft,
        };
      }
      // fallback
      return {
        width: window.innerWidth,
        height: window.innerHeight,
        offsetTop: 0,
        offsetLeft: 0,
      };
    },
  },
  async mounted() {
    await useAppStore().getAppInfo();
    window.addEventListener("mousemove", this.active);
    window.addEventListener("touchmove", this.active);
    if (PLATFORM === "kiosk")
      visualViewport.addEventListener("resize", () => {
        this.baseHeight = `${this.getSafeViewport().height}px`;
      });
    console.log(this.$route);
    if (this.shop) {
      document.title = `${this.$route.meta.title} | ${this.shop.name.normal}`;
      if (this.landingRestriction) {
        this.isLandingRestrictionOpen =
          this.landingRestriction.enabled &&
          window.sessionStorage.getItem("agreedRestriction") !== "true" &&
          window.localStorage.getItem("agreedRestriction") !== "true";
      }
    }
  },
  watch: {
    userInfo(newVal, oldVal) {
      if (!oldVal && newVal && PLATFORM === "embed") {
        H.identify(newVal.profile.email, {
          id: newVal._id,
        });
      }
    },
    isFirstInteractionPerformed(newVal) {
      if (newVal) {
        this.setIdleCheck();
      }
    },
    "$route.query"(newVal) {
      if (newVal && newVal["order-type"]) {
        localStorage.setItem("orderType", newVal["order-type"]);
      }
      if (newVal && newVal["table"]) {
        sessionStorage.setItem("table", newVal["table"]);
        sessionStorage.setItem("fromQR", "yes");
      }
    },
    "$route.path"(newVal) {
      if (newVal && this.shop) {
        document.title = `${this.$route.meta.title} | ${this.shop.name.normal}`;
      }
    },
  },
};
</script>
